import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import { PromoCode } from 'common/contracts';
import { Coupon, Offer } from 'common/contracts';
import dayjs from 'dayjs';
import { EStatusLabel } from 'design-system/StatusLabel/types';
import { EToasterStatus, showToast } from 'utils/showToast';

import {
  CouponFormValues,
  UpsertCouponDto,
  UpsertCouponWithPromoCodeDto,
  UpsertPromoCodeDto
} from './types';

export const STATUS_TO_ICON_MAP = {
  [EStatusLabel.SUCCESS]: <CheckCircleOutlineOutlinedIcon />,
  [EStatusLabel.FAILED]: <DoNotDisturbAltOutlinedIcon />,
  [EStatusLabel.PENDING]: <AccessTimeOutlinedIcon />
};

export function isDatePassed(timestamp: string) {
  return dayjs().isAfter(dayjs(timestamp));
}

export const generatePromoCodesDto = (
  promoCodes: PromoCode[],
  couponId?: string
): UpsertPromoCodeDto[] => {
  return promoCodes.map(({ name, maxRedemptions, active, id, fresh }) => {
    const data: UpsertPromoCodeDto = {
      name,
      maxRedemptions,
      active,
      couponId
    };

    if (id && !fresh) {
      data.id = id;
    }

    return data;
  });
};

export const generateCouponDto = (
  values: CouponFormValues,
  neverExpires: boolean
): UpsertCouponDto => {
  return {
    name: values.name,
    active: values.active,
    discountPercentage: Number(values.discountPercentage),
    maxRedemptionsPerCustomer: values.maxRedemptionsPerCustomer
      ? Number(values.maxRedemptionsPerCustomer)
      : null,
    expiredBy: neverExpires ? null : values.expiredBy,
    firstTimePurchase: values.firstTimePurchase,
    supportedOffersBySku: values.supportedOffersBySku,
    allowedPlayers: values.allowedPlayers
  };
};

export const checkUniqueness = (
  newName: string = '',
  coupons: Coupon[] | undefined,
  currentCouponName: string = '',
  isEdit: boolean
): boolean => {
  const couponsNames = coupons?.map((coupon) => coupon.name);
  if (isEdit) {
    return !couponsNames
      ?.filter((name) => name !== currentCouponName)
      .includes(newName);
  }
  return !couponsNames?.includes(newName);
};

export const getOffersForSelect = (
  offersMap: { bundles?: Offer[]; promotions?: Offer[]; popups?: Offer[] },
  supportedOffersByType: string[] = ['bundles', 'promotions', 'popups']
) => {
  const offers = [];
  if (offersMap.bundles && supportedOffersByType.includes('bundles')) {
    offers.push(
      ...offersMap.bundles.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.promotions && supportedOffersByType.includes('promotions')) {
    offers.push(
      ...offersMap.promotions.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.popups && supportedOffersByType.includes('popups')) {
    offers.push(
      ...offersMap.popups.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  return offers;
};

export const handleUpsertSuccess = async ({
  getSingleCoupon,
  helpers,
  isEdit
}: {
  getSingleCoupon: any;
  helpers: any;
  isEdit: boolean;
}) => {
  await getSingleCoupon.refetch();
  if (getSingleCoupon.data) {
    helpers.setValues({
      ...getSingleCoupon.data,
      promoCodes: (getSingleCoupon.data.promoCodes || []).map(
        (promoCode: any) => ({
          ...promoCode,
          fresh: false
        })
      )
    });
  }
  showToast({
    message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
    status: EToasterStatus.SUCCESS
  });
};

export const handleUpsertError = (error: any, isEdit: boolean) => {
  const serverError = error?.response?.data?.message;
  showToast({
    message: serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
    status: EToasterStatus.ERROR
  });
};
